import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchApi } from './fetchApi';

interface Lieferant {
  id: number;
  firmenname: string;
  kontaktperson: string | null;
  telefon: string | null;
  email: string | null;
  adresse: string | null;
}

interface FormDaten {
  firmenname: string;
  kontaktperson: string;
  telefon: string;
  email: string;
  adresse: string;
}

const leerForm: FormDaten = {
  firmenname: '',
  kontaktperson: '',
  telefon: '',
  email: '',
  adresse: '',
};

export default function LieferantFormular() {
  const [lieferanten, setLieferanten] = useState<Lieferant[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);

  // Modal: Anlegen / Bearbeiten
  const [modalOffen, setModalOffen] = useState(false);
  const [bearbeitenId, setBearbeitenId] = useState<number | null>(null);
  const [form, setForm] = useState<FormDaten>(leerForm);
  const [modalFehler, setModalFehler] = useState<string | null>(null);

  // Bestätigungsdialog: Löschen
  const [loeschenId, setLoeschenId] = useState<number | null>(null);
  const [loeschenName, setLoeschenName] = useState('');

  async function ladeLieferanten() {
    try {
      const res = await fetchApi('/api/lieferant');
      const data = await res.json();
      setLieferanten(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Fehler beim Laden der Lieferanten:', err);
    }
  }

  useEffect(() => {
    ladeLieferanten();
  }, []);

  function oeffneAnlegenModal() {
    setForm(leerForm);
    setBearbeitenId(null);
    setModalFehler(null);
    setModalOffen(true);
  }

  function oeffneBearbeitenModal(l: Lieferant) {
    setForm({
      firmenname: l.firmenname,
      kontaktperson: l.kontaktperson || '',
      telefon: l.telefon || '',
      email: l.email || '',
      adresse: l.adresse || '',
    });
    setBearbeitenId(l.id);
    setModalFehler(null);
    setModalOffen(true);
  }

  function schliesseModal() {
    setModalOffen(false);
  }

  function handleFormChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSpeichern() {
    setModalFehler(null);

    if (!form.firmenname.trim()) {
      setModalFehler('Firmenname ist erforderlich.');
      return;
    }

    try {
      const res = await fetchApi(
        bearbeitenId ? `/api/lieferant/${bearbeitenId}` : '/api/lieferant',
        {
          method: bearbeitenId ? 'PUT' : 'POST',
          body: JSON.stringify(form),
        }
      );
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        setModalFehler(data?.error || 'Fehler beim Speichern.');
        return;
      }

      setErfolg(bearbeitenId ? 'Lieferant aktualisiert.' : 'Lieferant angelegt.');
      setFehler(null);
      setModalOffen(false);
      ladeLieferanten();
    } catch (err: any) {
      setModalFehler(
        err?.message === '401'
          ? 'Session abgelaufen. Bitte neu einloggen.'
          : (err?.message || 'Unbekannter Fehler.')
      );
    }
  }

  function oeffneLoeschenDialog(l: Lieferant) {
    setLoeschenId(l.id);
    setLoeschenName(l.firmenname);
    setFehler(null);
    setErfolg(null);
  }

  function schliesseLoeschenDialog() {
    setLoeschenId(null);
    setLoeschenName('');
  }

  async function handleLoeschen() {
    if (!loeschenId) return;

    try {
      const res = await fetchApi(`/api/lieferant/${loeschenId}`, { method: 'DELETE' });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        setFehler(data?.error || 'Löschen nicht möglich.');
        schliesseLoeschenDialog();
        return;
      }

      setErfolg('Lieferant gelöscht.');
      schliesseLoeschenDialog();
      ladeLieferanten();
    } catch (err: any) {
      setFehler(
        err?.message === '401'
          ? 'Session abgelaufen. Bitte neu einloggen.'
          : (err?.message || 'Fehler beim Löschen.')
      );
      schliesseLoeschenDialog();
    }
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Lieferanten</Typography>
        <Button variant="contained" onClick={oeffneAnlegenModal}>
          + Lieferant anlegen
        </Button>
      </Box>

      {fehler && <Alert severity="error" sx={{ mb: 2 }}>{fehler}</Alert>}
      {erfolg && <Alert severity="success" sx={{ mb: 2 }}>{erfolg}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Firmenname</TableCell>
            <TableCell>Kontaktperson</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Telefon</TableCell>
            <TableCell align="right">Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lieferanten.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center">Keine Lieferanten vorhanden.</TableCell>
            </TableRow>
          ) : (
            lieferanten.map((l) => (
              <TableRow key={l.id} hover>
                <TableCell>{l.firmenname}</TableCell>
                <TableCell>{l.kontaktperson || '—'}</TableCell>
                <TableCell>{l.email || '—'}</TableCell>
                <TableCell>{l.telefon || '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => oeffneBearbeitenModal(l)} title="Bearbeiten">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => oeffneLoeschenDialog(l)} title="Löschen">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Modal: Anlegen / Bearbeiten */}
      <Dialog open={modalOffen} onClose={schliesseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{bearbeitenId ? 'Lieferant bearbeiten' : 'Lieferant anlegen'}</DialogTitle>
        <DialogContent>
          {modalFehler && <Alert severity="error" sx={{ mb: 2 }}>{modalFehler}</Alert>}
          <TextField
            label="Firmenname"
            name="firmenname"
            value={form.firmenname}
            onChange={handleFormChange}
            fullWidth
            required
            autoFocus
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            label="Kontaktperson"
            name="kontaktperson"
            value={form.kontaktperson}
            onChange={handleFormChange}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleFormChange}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Telefon"
            name="telefon"
            value={form.telefon}
            onChange={handleFormChange}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Adresse"
            name="adresse"
            value={form.adresse}
            onChange={handleFormChange}
            fullWidth
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={schliesseModal}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSpeichern}>
            {bearbeitenId ? 'Aktualisieren' : 'Anlegen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Löschen bestätigen */}
      <Dialog open={loeschenId !== null} onClose={schliesseLoeschenDialog}>
        <DialogTitle>Lieferant löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Soll <strong>{loeschenName}</strong> wirklich gelöscht werden?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={schliesseLoeschenDialog}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleLoeschen}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
